import { FC, useState, useEffect } from 'react';
import {
  TextField,
  StandardTextFieldProps,
  MenuItem,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

export interface ISelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface ISelectAsyncProps extends StandardTextFieldProps {
  name: string;
  apiRequest: () => Promise<any>;
  transformResponse: (response: any) => ISelectOption[];
  hasClear?: boolean;
  onClear?: () => void;
  handleOptions?: (data: ISelectOption[]) => void;
  showEmptyOption?: boolean;
  emptyOptionText?: string;
  refetch?: Array<any>;
  shouldFetch?: boolean;
  disabledText?: string;
  additionalActionOnChange?: () => void;
}

export const SelectAsync: FC<ISelectAsyncProps> = ({
  name,
  apiRequest,
  transformResponse,
  hasClear = false,
  onClear,
  handleOptions,
  showEmptyOption,
  emptyOptionText,
  refetch = [],
  shouldFetch = true,
  disabledText = '',
  additionalActionOnChange,
  ...props
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [options, setOptions] = useState<ISelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField(name);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await apiRequest();
        const data = transformResponse(response);
        setOptions(data);
        handleOptions && handleOptions(data);
      } catch (err) {
        enqueueSnackbar(`Error loading ${name} dropdown`, { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    if (shouldFetch) {
      fetchData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...refetch, shouldFetch]);

  const handleChange = (event: React.SyntheticEvent): void => {
    let target = event.target as HTMLInputElement;
    const { value } = target;
    setFieldValue && setFieldValue(name, value);
    additionalActionOnChange?.();
  };

  const config = {
    fullWidth: true,
    size: 'small' as 'small',
    ...field,
    value: field.value ?? '',
    input: field.value ?? '',
    ...props,
    select: true,
    label: isLoading ? 'Loading...' : props.label,
    onChange: props.onChange ?? handleChange,
    disabled: props.disabled || isLoading || false,
    InputProps: hasClear
      ? {
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                position: 'absolute',
                right: 27,
                cursor: 'pointer'
              }}
            >
              <IconButton
                component="span"
                title="Clear"
                size="small"
                onClick={() => {
                  setFieldValue(name, '');
                  onClear && onClear();
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }
      : undefined
  };

  if (meta.touched && meta.error) {
    config.error = true;
    config.helperText = meta.error;
  }

  return (
    <TextField {...config}>
      {showEmptyOption && !isLoading && options && options.length === 0 && (
        <MenuItem disabled>{emptyOptionText ?? 'No options available'}</MenuItem>
      )}
      {options?.map((option, index) => (
        <MenuItem
          key={`${option?.value}-${name}`}
          disabled={option?.disabled}
          value={option?.value}
          sx={option?.disabled ? { display: 'none' } : undefined}
        >
          {option?.label}
          {option?.disabled && ` ${disabledText}`}
        </MenuItem>
      ))}
    </TextField>
  );
};
